import { ThemeOptions, alertClasses, inputClasses } from '@mui/material';
import { buttonClasses } from '@mui/material/Button';
import { createTheme } from '@mui/material/styles';

import { ColorScale } from '@/theme/colors';
import { darkPalette } from '@/theme/darkPalette';
import { lightPalette } from '@/theme/lightPalette';

const fonts = ['Inter', '-apple-system', 'Helvetica Neue', 'sans-serif'].join(',');
const monoFonts = ['IBM Plex Mono', 'monaco', 'monospace'].join(',');
const marketingFonts = ['BDO', 'Inter', 'Helvetica Neue', 'sans-serif'].join(',');

// For backward compatibility
const defaultPalette = lightPalette;

const defaultMUItheme = createTheme();

const createThemeConfig = (palette: typeof lightPalette) => ({
  breakpoints: defaultMUItheme.breakpoints,
  palette,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
    '0px 2px 8px 0px rgba(0, 0, 0, 0.08)',
    '0px 4px 10px rgba(0, 0, 0, 0.12)',
    '0px 6px 12px rgba(0, 0, 0, 0.14)',
    ...Array(20).fill('none'),
  ],
  components: {
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': {
            cursor: 'pointer',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        actions: {
          color: palette.text.default,
          '& .MuiIconButton-root': {
            color: 'inherit',
            '&.Mui-disabled': {
              color: palette.text.subtle,
            },
          },
        },
      },
    },
    MuiFab: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiTabPanel: {
      styleOverrides: {
        root: {
          paddingTop: 0,
          paddingBottom: 0,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'medium',
        variant: 'outlined',
        color: 'secondary',
        disableElevation: true,
        disableFocusRipple: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxSizing: 'border-box',
          minWidth: 'auto',
          transition: 'all 0.15s ease-in-out',

          [`&.${buttonClasses.disabled}:not(.${buttonClasses.text}, .${buttonClasses.loading})`]: {
            color: palette.text.subtle,
            background: palette.background.muted,
            border: `1px solid ${palette.border.default}`,
          },
          '&.MuiButton-loading': {
            color: palette.text.subtle,
            '& .MuiButton-loadingIndicator': {
              color: palette.text.subtle,
            },
          },
        },
        iconSizeMedium: {
          '> .MuiSvgIcon-fontSizeMedium': {
            fontSize: '16px',
          },
        },
        startIcon: {
          marginLeft: 0,
        },
        endIcon: {
          marginRight: 0,
        },
        sizeSmall: {
          fontSize: '.75rem',
          lineHeight: '16px',
          padding: '6px 12px',
        },
        sizeMedium: {
          fontSize: '.875rem',
          lineHeight: '16px',
          padding: '7px 12px',
        },
        sizeLarge: {
          fontSize: '1rem',
          lineHeight: '32px',
          padding: '8px 24px',
        },
        outlined: {
          color: palette.text.default,
          background: palette.background.default,
        },
        containedPrimary: {
          color: ColorScale.mono.white,
          background: palette.background.successEmphasis,
          border: `1px solid ${palette.border.successEmphasis}`,

          '&:hover, &:focus': {
            background: ColorScale.green[700],
            border: `1px solid ${ColorScale.green[800]}`,
          },
        },
        containedSecondary: {
          color: ColorScale.mono.white,
          background: palette.background.emphasis,
          border: `1px solid ${palette.border.emphasis}`,

          '&:hover, &:focus': {
            border: `1px solid ${ColorScale.gray[800]}`,
            background: ColorScale.gray[700],
          },
        },
        containedWarning: {
          color: ColorScale.mono.white,
          background: palette.background.warnEmphasis,
          border: `1px solid ${ColorScale.yellow[600]}`,

          '&:hover, &:focus': {
            background: ColorScale.yellow[600],
            border: `1px solid ${ColorScale.yellow[700]}`,
          },
        },
        containedInfo: {
          color: ColorScale.mono.white,
          background: palette.background.infoEmphasis,
          border: `1px solid ${palette.border.infoEmphasis}`,

          '&:hover, &:focus': {
            background: ColorScale.blue[600],
            border: `1px solid ${ColorScale.blue[700]}`,
          },
        },
        containedSuccess: {
          color: ColorScale.mono.white,
          background: palette.background.successEmphasis,
          border: `1px solid ${palette.border.successEmphasis}`,

          '&:hover, &:focus': {
            background: ColorScale.green[700],
            border: `1px solid ${ColorScale.green[800]}`,
          },
        },
        containedError: {
          color: ColorScale.mono.white,
          background: palette.background.dangerEmphasis,
          border: `1px solid ${palette.border.dangerEmphasis}`,

          '&:hover, &:focus': {
            background: ColorScale.red[600],
            border: `1px solid ${ColorScale.red[700]}`,
          },
        },
        outlinedPrimary: {
          background: palette.background.default,
          border: `1px solid ${palette.border.default}`,
          '&:hover, &:focus': {
            background: palette.background.default,
            border: `1px solid ${palette.border.emphasis}`,
          },
          '.MuiButton-icon': {
            color: palette.text.subtle,
          },
        },
        outlinedSecondary: {
          background: palette.background.default,
          border: `1px solid ${palette.border.default}`,
          '&:hover, &:focus': {
            background: palette.background.default,
            border: `1px solid ${palette.border.emphasis}`,
          },
          '.MuiButton-icon': {
            color: palette.text.subtle,
          },
        },
        outlinedSuccess: {
          color: palette.text.success,
          border: `1px solid ${palette.border.success}`,
          '&:hover, &:focus': {
            background: ColorScale.green[50],
            border: `1px solid ${palette.border.successEmphasis}`,
          },
        },
        outlinedWarning: {
          color: palette.text.warn,
          border: `1px solid ${palette.border.warn}`,
          '&:hover, &:focus': {
            background: ColorScale.yellow[50],
            border: `1px solid ${palette.border.warnEmphasis}`,
          },
        },
        outlinedError: {
          color: palette.text.danger,
          border: `1px solid ${palette.border.danger}`,
          '&:hover, &:focus': {
            background: ColorScale.red[50],
            border: `1px solid ${palette.border.dangerEmphasis}`,
          },
        },
        outlinedInfo: {
          color: palette.text.accent,
          border: `1px solid ${palette.border.info}`,
          '&:hover, &:focus': {
            background: ColorScale.blue[50],
            border: `1px solid ${palette.border.infoEmphasis}`,
          },
        },
        textSecondary: {
          color: palette.text.muted,
          '&:hover, &:focus': {
            color: palette.text.default,
            background: palette.background.muted,
          },
        },
        textPrimary: {
          color: palette.text.default,

          '&:hover, &:focus': {
            background: palette.background.muted,
          },
        },
        textSuccess: {
          color: palette.text.success,

          '&:hover, &:focus': {
            background: palette.background.success,
          },
          svg: {
            color: palette.text.success,
          },
        },
        textError: {
          color: palette.text.danger,

          '&:hover, &:focus': {
            background: palette.background.danger,
          },
        },
        textInfo: {
          color: palette.text.accent,

          '&:hover, &:focus': {
            background: palette.background.info,
          },
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: palette.text.subtle,
          borderRadius: '8px',

          '&:hover, &:focus': {
            color: palette.text.default,
            background: palette.background.muted,
          },
          '&.Mui-disabled': {
            color: palette.text.subtle,
          },
        },
        sizeSmall: {
          padding: '4px',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          border: `1px solid ${palette.border.default}`,
          overflow: 'hidden',
        },
      },
    },
    MuiToggleButton: {
      defaultProps: {
        disableFocusRipple: true,
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          color: palette.text.default,
          textTransform: 'none',
          padding: '4px 12px',
          borderRadius: 0,

          '&.Mui-selected': {
            color: palette.text.default,
            backgroundColor: palette.background.muted,
            '&:not(:first-of-type)': {
              borderLeft: `1px solid ${palette.border.muted}`,
            },
            '&.MuiToggleButtonGroup-firstButton': {
              borderTopLeftRadius: '7px',
              borderBottomLeftRadius: '7px',
            },
            '&:hover': {
              background: palette.background.muted,
              color: palette.text.default,
            },
          },
          '&.Mui-disabled': {
            color: palette.text.subtle,
          },
          '&.MuiToggleButtonGroup-grouped': {
            '&:not(:last-of-type)': {
              border: `0px solid ${palette.border.emphasis}`,
            },
            '&:not(:first-of-type)': {
              border: `0px solid ${palette.border.default}`,
            },
          },
          '&:hover': {
            color: palette.text.default,
            background: palette.background.subtle,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          marginLeft: '0px',
          marginRight: '0px',
          '&.Mui-disabled': {
            color: palette.text.subtle,
            '& .MuiTypography-root': {
              color: palette.text.subtle,
            },
          },
        },
        label: {
          fontSize: '12px',
          fontWeight: '500',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: '500',
          marginBottom: '4px',
          color: palette.text.default,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          flex: 'auto',
          borderColor: palette.border.default,
          boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
        },
        columnHeaders: {
          backgroundColor: palette.background.subtle,
          borderColor: palette.border.default,
          fontSize: '14px',
        },
        footerContainer: {
          backgroundColor: palette.background.subtle,
          borderColor: palette.border.default,
          borderBottomLeftRadius: '6px',
          borderBottomRightRadius: '6px',
        },
        columnHeader: {
          padding: '0 16px',
          '&:focus': {
            outline: 'none',
          },
        },
        row: ({ isClickable = true }) => ({
          ...(isClickable && {
            '&:hover': {
              backgroundColor: palette.background.subtle,
              cursor: 'pointer',
            },
          }),
        }),
        'cell--withRenderer': {
          padding: '16px',

          '&:focus-within, &:focus': {
            outline: 'none',
          },
        },
        withBorderColor: {
          borderColor: palette.border.default,
        },
        iconButtonContainer: {
          marginLeft: '4px',
        },
        cell: {
          borderColor: palette.border.default,
          fontSize: '14px',
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: {
          color: palette.text.default,
          '&.Mui-disabled:not(.Mui-selected)': {
            color: palette.text.subtle,
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
        size: 'small',
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          width: '36px',
          height: '20px',
          padding: 0,
          margin: '0px 0px 0px 8px',
          overflow: 'unset',
        },
        switchBase: {
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: palette.background.infoEmphasis,
            border: `solid 1px ${palette.border.accentEmphasis}`,
          },
          '&.Mui-checked > .MuiSwitch-thumb': {
            backgroundColor: palette.background.default,
          },
          '.MuiSwitch-thumb': {
            width: '12px',
            height: '12px',
          },
          '&:hover': {
            backgroundColor: 'inherit',
          },
        },
        track: {
          borderRadius: 40,
          border: `solid 1px ${palette.border.default}`,
          backgroundColor: palette.background.default,
          opacity: 1,
          transition: 'all 0.1s ease-in-out',
          boxSizing: 'border-box',
        },
        thumb: {
          boxShadow: 'none',
          backgroundColor: palette.background.muted,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          paddingLeft: 0,
          input: {
            height: 'auto',
          },
        },
        multiline: {
          padding: '7px 12px',
        },
        notchedOutline: {
          border: 'none',
        },
        sizeSmall: {
          padding: '6px',
        },
        input: {
          '&.Mui-disabled': {
            color: palette.text.subtle,
            WebkitTextFillColor: palette.text.subtle,
          },
          '::placeholder': {
            color: palette.text.subtle,
          },
          '-webkit-input-placeholder': {
            color: palette.text.subtle,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: palette.background.subtle,
          border: `1px solid ${palette.border.default}`,
          color: palette.text.muted,
          fontWeight: 400,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: palette.text.default,
          lineHeight: '16px',
          borderRadius: '8px',
          background: palette.background.default,
          border: `1px solid ${palette.border.default}`,
          input: {
            lineHeight: '16px',
            padding: '7px 12px',
          },

          [`&.${inputClasses.focused}`]: {
            border: `1px solid ${palette.border.emphasis}`,
          },
        },

        sizeSmall: {
          fontSize: '12px',
          maxHeight: '28px',
          padding: '6px',
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          marginRight: '-4px',
          marginLeft: '8px',
          color: palette.text.subtle,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        inputRoot: {
          padding: '6px 12px',
        },
        noOptions: {
          padding: '8px 16px',
          fontSize: '14px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        root: {
          '> .MuiInputBase-root': {
            '&.MuiAutocomplete-inputRoot': {
              input: {
                padding: 0,
              },
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: palette.text.default,
          padding: '7px 12px',
          minHeight: 'auto',
        },
        icon: {
          color: palette.text.subtle,
          right: '8px',
          top: 'auto',
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        valueLabel: {
          background: 'transparent',
          color: ColorScale.gray[900],
          '&.MuiSlider-valueLabelOpen': {
            transform: 'translateY(-60%) scale(1)',
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: '14px',
        },
        separator: {
          marginLeft: '4px',
          marginRight: '4px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.default,
          border: `1px solid ${palette.border.default}`,
          boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${palette.border.default}`,
          background: palette.background.subtle,
          padding: '16px 24px',
          justifyContent: 'end',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          padding: 0,
          marginRight: '4px',
          '&.Mui-disabled': {
            color: palette.text.subtle,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: palette.border.default,
          backgroundColor: palette.background.default,
          padding: 0,
        },
      },
    },
    MuiChip: {
      defaultProps: {
        color: 'secondary',
      },
      styleOverrides: {
        colorPrimary: {
          color: ColorScale.mono.white,
          borderColor: palette.border.emphasis,
          background: palette.background.emphasis,
        },
        colorSuccess: {
          color: palette.text.success,
          borderColor: palette.border.successEmphasis,
          background: palette.background.success,
        },
        colorDefault: {
          color: palette.text.muted,
          borderColor: palette.border.default,
          background: palette.background.subtle,
        },
        colorSecondary: {
          color: palette.text.muted,
          borderColor: palette.border.default,
          background: palette.background.subtle,
        },
        colorError: {
          color: palette.text.danger,
          borderColor: palette.border.dangerEmphasis,
          background: palette.background.danger,
        },
        colorWarning: {
          color: palette.text.warn,
          borderColor: palette.border.warnEmphasis,
          background: palette.background.warn,
        },
        colorInfo: {
          color: palette.text.accent,
          borderColor: palette.border.infoEmphasis,
          background: palette.background.info,
        },
        sizeSmall: {
          height: 'auto',
          border: '1px solid',
          borderRadius: 8,
          padding: '3px 6px',
          fontSize: 12,
          fontWeight: 500,
          display: 'flex',
          gap: 4,
          lineHeight: '14px',
        },
        iconSmall: {
          marginLeft: 0,
          marginRight: 0,
          width: 14,
          height: 14,
        },
        avatarSmall: {
          marginLeft: 0,
          marginRight: 0,
          width: 14,
          height: 14,
          color: 'inherit',
        },
        labelSmall: {
          padding: '0',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      // TODO (VAHAN) this is a temporary configuration
      styleOverrides: {
        content: {
          margin: '16px 0',
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: 0,
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'inherit',
          marginRight: 16,
        },
      },
    },
    MuiList: {
      defaultProps: {
        disablePadding: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          'li:first-of-type': {
            borderTopRightRadius: 6,
            borderTopLeftRadius: 6,
          },
        },
      },
    },
    MuiListItem: {
      defaultProps: {
        dense: true,
      },
      styleOverrides: {
        root: {
          maxHeight: 62,
          'a.MuiListItemButton-root:hover': {
            color: 'inherit',
          },
          '&.Mui-selected': {
            color: palette.text.emphasis,
            borderRadius: '8px',
            background: palette.background.infoEmphasis,
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          maxHeight: 62,
          lineHeight: 'inherit',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: 12,
          marginBottom: 4,
        },
        secondary: {
          fontSize: 12,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          padding: '8px 16px',
          borderRadius: '8px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          textAlign: 'left',
          fontWeight: '500',
          transition: 'background 0.15s ease-in-out',
          color: palette.text.muted,
          minWidth: 'auto',
          padding: '0',
          marginRight: '24px',

          '&:last-of-type': {
            marginRight: '0px',
          },

          '&.Mui-selected': {
            color: palette.text.default,
          },
          '&:hover': {
            color: palette.text.default,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: palette.border.successEmphasis,
          height: '3px',
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: false,
      },
      styleOverrides: {
        paper: {
          border: `1px solid ${palette.border.default}`,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        root: {
          backgroundColor: palette.background.default,
          marginTop: '4px',
          '&.MuiTableContainer-root': {
            boxShadow: `
              0px 0px 0px -1px rgb(0 0 0 / 20%),
              0px 0px 1px 0px rgb(0 0 0 / 14%),
              0px 1px 2px 0px rgb(0 0 0 / 12%)
            `,
          },
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        paper: {
          backgroundColor: palette.background.default,
          border: `1px solid ${palette.border.muted}`,
          borderRadius: 8,
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        list: {
          padding: 4,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          padding: '6px 8px',
          borderRadius: '8px',
          minWidth: '150px',
          '&:not(:last-of-type)': {
            marginBottom: 4,
          },
          '&.Mui-disabled.Mui-selected': {
            background: palette.background.default,
            color: palette.text.muted,
          },
          '&.Mui-selected': {
            background: palette.background.muted,
            '&:hover': {
              background: palette.background.muted,
            },
          },
          '&.Mui-focusVisible': {
            backgroundColor: `${palette.background.muted} !important`,
          },
          '&:hover': {
            background: palette.background.muted,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: palette.border.muted,
          marginTop: '4px !important',
          marginBottom: '4px !important',
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: palette.background.muted,
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${palette.background.subtle}, transparent)`,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        action: {
          padding: 0,
        },
        standardWarning: {
          backgroundColor: palette.background.warn,
          border: `1px solid ${palette.border.warnEmphasis} !important`,
        },
      },
      variants: [
        {
          props: { variant: 'subtle' },
          style: {
            background: palette.background.subtle,
            color: palette.text.default,

            [`& .${alertClasses.icon}`]: {
              color: palette.text.subtle,
              opacity: 1,
            },
          },
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // This disables the calt feature in the Inter font, which causes "x" to be rendered as "×" in some cases
          fontFeatureSettings: "'calt' 0",
        },
      },
    },
  },
  typography: {
    title: {
      fontSize: 40,
      lineHeight: '1.1em',
      fontWeight: 500,
      fontFamily: marketingFonts,
    },
    h1: {
      fontSize: '32px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '40px',
    },
    h2: {
      fontSize: '28px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '32px',
    },
    h3: {
      fontSize: '22px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '25px',
    },
    h4: {
      fontSize: '18px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '21px',
    },
    h5: {
      fontSize: '14px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '16px',
    },
    h6: {
      fontSize: '12px',
      fontWeight: 500,
      color: palette.text.default,
      margin: 0,
      lineHeight: '14px',
    },
    body1: {
      fontSize: '14px',
      color: palette.text.default,
    },
    body2: {
      fontSize: '12px',
      color: palette.text.default,
    },
    mono1: {
      fontSize: '14px',
      color: palette.text.default,
      fontFamily: monoFonts,
      letterSpacing: '-0.28px',
    },
    mono2: {
      fontSize: '12px',
      color: palette.text.default,
      fontFamily: monoFonts,
      letterSpacing: '-0.28px',
    },
    subtitle1: {
      fontSize: '16px',
      color: palette.text.default,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '14px',
      color: palette.text.default,
      fontWeight: 500,
    },
    caption: {
      fontSize: '12px',
      color: palette.text.default,
      fontWeight: 500,
    },
    display1: {
      fontSize: '16px',
      color: palette.text.default,
      lineHeight: '1.6em',
      fontFamily: marketingFonts,
    },
    display2: {
      fontSize: '14px',
      color: palette.text.default,
      lineHeight: '1.14em',
      fontFamily: marketingFonts,
    },
    fontFamily: fonts,
    monoFontFamily: monoFonts,
  },
  zIndex: {
    // this is following the MUI zindex values (https://mui.com/customization/z-index/)
    // sits between the modal z-index and the snackbar z-index values
    autocomplete: 1350,
    graphActions: 6,
  },
  cssVariables: true,
});

// For backward compatibility
const defaultThemeConfig = createThemeConfig(defaultPalette);

type BasetenTheme = typeof defaultThemeConfig;

/**
 * Creates a theme based on the specified mode (light or dark)
 * @param mode - 'light' or 'dark'
 * @returns A complete MUI theme
 */
export const createAppTheme = (mode: 'light' | 'dark') => {
  const palette = mode === 'light' ? lightPalette : darkPalette;
  const themeConfig = createThemeConfig(palette);
  return createTheme(themeConfig as unknown as ThemeOptions);
};

// For backward compatibility, export a default light theme
const defaultTheme = createAppTheme('light');

export {
  BasetenTheme,
  defaultPalette,
  defaultTheme,
  defaultThemeConfig,
  fonts,
  marketingFonts,
  monoFonts,
};
